import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useUITranslation } from '../../../services/uiTranslations/reactBindings/hooks';
import { useChatSlice } from '../../../store/slices/chat/selectors';
import { MessageType } from '../../../store/slices/chat/types';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { useIsLargerThanWide } from '../../../styles/hooks/media';
import { getIsSupportMessage, parseSystemMessage } from '../../../utils/chat';
import { Ribbon } from '../../ribbon/Ribbon';
import { MessageContentConnected } from '../chat/messages/messageContent/MessageContentConnected';

const HIDE_DELAY = 3_000;

export const ChatRibbonConnected: FC = (): ReactNode => {
  const [isVisible, setIsVisible] = useState(true);

  const hasWinners = useGameStateSlice.winners().length;

  const message = useChatSlice.getLastChatMessage();
  const hostText = useUITranslation('GAME_HOST');
  const supportText = useUITranslation('LIVE_SUPPORT');
  const messageContent = message?.content;
  const requireTranslation = message?.requireTranslation;
  const messageType = message?.messageType;
  const messageUser = message?.userScreenName;
  const messageId = message?.messageId;
  const messageParams = message?.messageParams;
  const isWide = useIsLargerThanWide();
  const isChatDisabled = useConfigSlice.isChatDisabled();
  const currentUserName = useConfigSlice.playerConfig()?.screenName;

  const label = useMemo(() => {
    let label = messageUser;

    if (messageType === MessageType.Host) {
      label = hostText;
    } else if (getIsSupportMessage(messageType)) {
      label = supportText;
    }

    return label ? `${label}:` : '';
  }, [hostText, messageType, messageUser, supportText]);

  // Hide component if last message hasn't been updated for 3000ms
  useEffect(() => {
    let timeoutId: number;

    if (messageContent) {
      setIsVisible(true);
      timeoutId = window.setTimeout(() => {
        setIsVisible(false);
      }, HIDE_DELAY);
    }

    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [messageContent]);

  if (
    isWide ||
    isChatDisabled ||
    hasWinners ||
    !isVisible ||
    !messageContent ||
    messageType === MessageType.SupportUser
  ) {
    return null;
  }

  const generateMessageContent = (): ReactNode => {
    if (messageType === MessageType.System) {
      return parseSystemMessage(messageContent); // Remove HTML tags from system message
    }

    const dynamicValues =
      messageType === MessageType.Host && messageId === 'dealer_welcome_message'
        ? { username: currentUserName || '' }
        : messageParams;

    return (
      <MessageContentConnected
        content={messageContent}
        requireTranslation={requireTranslation}
        dynamicValues={dynamicValues}
      />
    );
  };

  return (
    <Ribbon label={label} onewayFade>
      {generateMessageContent()}
    </Ribbon>
  );
};
